import React, { memo, useMemo, type FC, type ReactNode } from 'react';
import { View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui/text';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { getSpacing, type SpacingToken } from '@/theme/spacing';
import type { TypographyToken } from '@/theme/typography';

export type LabeledDividerProps = {
  /** Centered label between the two lines. When omitted, renders a plain divider. */
  children?: ReactNode;
  /** Line color. */
  color?: string;
  /** Line thickness in design px (auto-scaled). Default 1. */
  thickness?: number;
  /** Vertical margin around the divider. Spacing token or number. */
  spacing?: SpacingToken | number;
  /** Gap between each line and the label in design px (auto-scaled). Default 8. */
  gap?: number;
  /** Typography variant for the label. */
  textVariant?: TypographyToken;
  /** Label color. */
  textColor?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * LabeledDivider – a horizontal rule with an optional centered label
 * (line — label — line), e.g. the "or login with" separator.
 */
const LabeledDividerComponent: FC<LabeledDividerProps> = ({
  children,
  color = colors['5'],
  thickness = 1,
  spacing,
  gap = 8,
  textVariant = 'Font Inter/Subtitle/Regular 12px',
  textColor = colors['6'],
  style,
}) => {
  const { scale } = useScaling();

  const lineStyle = useMemo<ViewStyle>(
    () => ({ flex: 1, height: scale(thickness), backgroundColor: color }),
    [scale, thickness, color],
  );

  const containerStyle = useMemo<ViewStyle>(
    () => ({ marginVertical: spacing !== undefined ? getSpacing(spacing) : 0 }),
    [spacing],
  );

  const labelStyle = useMemo<TextStyle>(() => ({ color: textColor }), [textColor]);

  return (
    <Stack
      direction="row"
      align="center"
      width="100%"
      gap={scale(gap)}
      style={[containerStyle, style]}>
      <View style={lineStyle} />
      {children != null && (
        <Text variant={textVariant} style={labelStyle}>
          {children}
        </Text>
      )}
      <View style={lineStyle} />
    </Stack>
  );
};

export const LabeledDivider = memo(LabeledDividerComponent);

export default LabeledDivider;
